/**
 * Notifications Service
 * 
 * Provides CRUD operations and real-time subscriptions for user notifications
 */

import { supabase } from './supabaseClient';
import type { Notification } from '../types';
import { Logger } from '../utils/logger';

export type NotificationType = 'mensaje' | 'pago' | 'aviso' | 'sistema' | 'alerta';

export interface CreateNotificationParams {
    id_usuario: number;
    tipo: NotificationType;
    titulo: string;
    mensaje: string;
    link?: string;
    metadata?: Record<string, any>;
}

/**
 * Get notifications for a user
 */
export const getNotifications = async (
    userId: number,
    limit: number = 20,
    offset: number = 0,
    onlyUnread: boolean = false
): Promise<Notification[]> => {
    try {
        let query = supabase
            .from('notificaciones')
            .select('*')
            .eq('id_usuario', userId)
            .order('fecha_creacion', { ascending: false })
            .range(offset, offset + limit - 1);

        if (onlyUnread) {
            query = query.eq('leida', false);
        }

        const { data, error } = await query;

        if (error) throw error;
        return (data || []) as Notification[];
    } catch (error) {
        Logger.error('Error fetching notifications', error);
        return [];
    }
};

/**
 * Get count of unread notifications
 */
export const getUnreadCount = async (userId: number): Promise<number> => {
    try {
        const { count, error } = await supabase
            .from('notificaciones')
            .select('*', { count: 'exact', head: true })
            .eq('id_usuario', userId)
            .eq('leida', false);

        if (error) throw error;
        return count || 0;
    } catch (error) {
        Logger.error('Error getting unread count', error);
        return 0;
    }
};

/**
 * Mark a notification as read
 */
export const markAsRead = async (notificationId: number): Promise<boolean> => {
    try {
        const { error } = await supabase
            .from('notificaciones')
            .update({ leida: true })
            .eq('id_notificacion', notificationId);

        if (error) throw error;
        return true;
    } catch (error) {
        Logger.error('Error marking notification as read', error);
        return false;
    }
};

/**
 * Mark all notifications as read for a user
 */
export const markAllAsRead = async (userId: number): Promise<boolean> => {
    try {
        const { error } = await supabase
            .from('notificaciones')
            .update({ leida: true })
            .eq('id_usuario', userId)
            .eq('leida', false);

        if (error) throw error;
        return true;
    } catch (error) {
        Logger.error('Error marking all notifications as read', error);
        return false;
    }
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId: number): Promise<boolean> => {
    try {
        const { error } = await supabase
            .from('notificaciones')
            .delete()
            .eq('id_notificacion', notificationId);

        if (error) throw error;
        return true;
    } catch (error) {
        Logger.error('Error deleting notification', error);
        return false;
    }
};

/**
 * Create a new notification
 */
export const createNotification = async (params: CreateNotificationParams): Promise<Notification | null> => {
    try {
        const { data, error } = await supabase
            .from('notificaciones')
            .insert({
                id_usuario: params.id_usuario,
                tipo: params.tipo,
                titulo: params.titulo,
                mensaje: params.mensaje,
                link: params.link,
                metadata: params.metadata,
                leida: false,
                fecha_creacion: new Date().toISOString()
            })
            .select()
            .maybeSingle();

        if (error) {
            Logger.error('Error creating notification', error);
            return null;
        }

        return data as Notification;
    } catch (error) {
        console.error('Error creating notification:', error);
        return null;
    }
};

/**
 * Subscribe to real-time notifications for a user
 * Returns a cleanup function to unsubscribe
 */
export const subscribeToNotifications = (
    userId: number,
    onNewNotification: (notification: Notification) => void,
    onError?: (error: any) => void
): (() => void) => {
    const channel = supabase
        .channel(`notifications:${userId}`)
        .on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'notificaciones',
                filter: `id_usuario=eq.${userId}`
            },
            (payload) => {
                if (payload.new) {
                    onNewNotification(payload.new as Notification);
                }
            }
        )
        .on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'notificaciones',
                filter: `id_usuario=eq.${userId}`
            },
            (payload) => {
                // Handle updates (e.g., marking as read)
                if (payload.new) {
                    onNewNotification(payload.new as Notification);
                }
            }
        )
        .subscribe((status) => {
            if (status === 'SUBSCRIBED') {
                Logger.debug('Subscribed to notifications');
            } else if (status === 'CHANNEL_ERROR') {
                Logger.error('Error subscribing to notifications');
                onError?.(new Error('Failed to subscribe to notifications'));
            }
        });

    // Return cleanup function
    return () => {
        channel.unsubscribe();
    };
};

/**
 * Helper to create notification for aviso approval
 */
export const notifyAvisoApproved = async (
    userId: number,
    avisoId: number,
    avisoTitulo: string
): Promise<void> => {
    await createNotification({
        id_usuario: userId,
        tipo: 'aviso',
        titulo: '¡Aviso Aprobado!',
        mensaje: `Tu aviso "${avisoTitulo}" ha sido aprobado y ya está visible para todos.`,
        link: `/aviso/${avisoId}`,
        metadata: { id_aviso: avisoId }
    });
};

/**
 * Helper to create notification for aviso rejection
 */
export const notifyAvisoRejected = async (
    userId: number,
    avisoId: number,
    avisoTitulo: string,
    razon?: string
): Promise<void> => {
    await createNotification({
        id_usuario: userId,
        tipo: 'aviso',
        titulo: 'Aviso Rechazado',
        mensaje: `Tu aviso "${avisoTitulo}" fue rechazado.${razon ? ` Razón: ${razon}` : ''}`,
        link: `/mis-avisos`,
        metadata: { id_aviso: avisoId, razon }
    });
};

/**
 * Helper to create notification for payment confirmation
 */
export const notifyPaymentConfirmed = async (
    userId: number,
    pagoId: number,
    monto: number,
    concepto: string
): Promise<void> => {
    await createNotification({
        id_usuario: userId,
        tipo: 'pago',
        titulo: '¡Pago Confirmado!',
        mensaje: `Tu pago de $${monto.toFixed(2)} por "${concepto}" fue procesado exitosamente.`,
        link: `/mis-compras`,
        metadata: { id_pago: pagoId, monto, concepto }
    });
};

/**
 * Helper to create notification for new message
 */
export const notifyNewMessage = async (
    userId: number,
    senderName: string,
    conversacionId: number,
    preview: string
): Promise<void> => {
    await createNotification({
        id_usuario: userId,
        tipo: 'mensaje',
        titulo: `Nuevo mensaje de ${senderName}`,
        mensaje: preview.substring(0, 100),
        link: `/conversacion/${conversacionId}`,
        metadata: { id_conversacion: conversacionId }
    });
};

/**
 * Helper to create notification for search alert match
 */
export const notifySearchAlertMatch = async (
    userId: number,
    avisoId: number,
    avisoTitulo: string,
    alertaTitulo: string
): Promise<void> => {
    await createNotification({
        id_usuario: userId,
        tipo: 'alerta',
        titulo: `🔔 Nueva coincidencia: ${alertaTitulo}`,
        mensaje: `Encontramos un nuevo aviso que coincide con tu alerta: "${avisoTitulo}"`,
        link: `/aviso/${avisoId}`,
        metadata: { id_aviso: avisoId }
    });
};

/**
 * Helper to create notification for store plan approval
 */
export const notifyStorePlanApproved = async (
    userId: number,
    planName: string,
    storeName: string,
    durationDays: number
): Promise<void> => {
    await createNotification({
        id_usuario: userId,
        tipo: 'sistema',
        titulo: '¡Plan de Tienda Aprobado!',
        mensaje: `El cambio de plan a "${planName}" para tu tienda "${storeName}" ha sido aprobado. Duración: ${durationDays} días.`,
        link: `/mi-tienda`,
        metadata: { plan: planName, store: storeName }
    });
};

/**
 * Helper to create notification for store plan rejection
 */
export const notifyStorePlanRejected = async (
    userId: number,
    storeName: string
): Promise<void> => {
    await createNotification({
        id_usuario: userId,
        tipo: 'sistema',
        titulo: 'Solicitud de Plan Rechazada',
        mensaje: `La solicitud de cambio de plan para tu tienda "${storeName}" no ha sido aprobada.`,
        link: `/mi-tienda`,
        metadata: { store: storeName }
    });
};

/**
 * Helper to create notification for new order (store owner)
 */
export const notifyNewOrder = async (
    storeOwnerId: number,
    orderId: number,
    amount: number,
    storeName: string
): Promise<void> => {
    await createNotification({
        id_usuario: storeOwnerId,
        tipo: 'mensaje', // Using 'mensaje' icon or type for now, could be 'aviso'
        titulo: '¡Nueva Venta!',
        mensaje: `Has recibido un nuevo pedido en "${storeName}" por $${amount}.`,
        link: `/mi-tienda/pedidos`,
        metadata: { id_pedido: orderId, store: storeName }
    });
};

/**
 * Helper to create notification for private store access request (Store Owner)
 */
export const notifyStoreAccessRequested = async (
    ownerId: number,
    requesterName: string,
    storeName: string
): Promise<void> => {
    await createNotification({
        id_usuario: ownerId,
        tipo: 'sistema',
        titulo: '🔒 Solicitud de Acceso',
        mensaje: `${requesterName} solicita acceso a tu tienda privada "${storeName}".`,
        link: `/mi-tienda/configuracion`, // Directs to access management tab ideally
        metadata: { store: storeName, requester: requesterName }
    });
};

/**
 * Helper to create notification for private store access decision (Requester)
 */
export const notifyStoreAccessDecision = async (
    userId: number,
    storeName: string,
    status: 'aprobado' | 'rechazado' | 'bloqueado'
): Promise<void> => {
    if (status === 'bloqueado') return; // Don't notify if blocked? Or maybe yes. usually silent.

    const title = status === 'aprobado' ? '✅ Acceso Aprobado' : '❌ Acceso Rechazado';
    const message = status === 'aprobado'
        ? `Tu solicitud de acceso a "${storeName}" ha sido aprobada.`
        : `Tu solicitud de acceso a "${storeName}" ha sido rechazada.`;
    const link = status === 'aprobado' ? `/tienda/${storeName.toLowerCase().replace(/\s+/g, '-')}` : `/`;

    await createNotification({
        id_usuario: userId,
        tipo: 'sistema',
        titulo: title,
        mensaje: message,
        link: link,
        metadata: { store: storeName, status }
    });
};

/**
 * Helper to create notification for order status update (Customer)
 */
export const notifyOrderStatusUpdate = async (
    userId: number,
    orderNumber: string,
    storeName: string,
    newStatus: string
): Promise<void> => {
    let title = `Actualización de Pedido #${orderNumber}`;
    let message = `El estado de tu pedido en "${storeName}" cambió a: ${newStatus}.`;

    if (newStatus === 'confirmado') title = '✅ Pedido Confirmado';
    if (newStatus === 'enviado') title = '🚚 Pedido Enviado';
    if (newStatus === 'entregado') title = '🎉 Pedido Entregado';
    if (newStatus === 'cancelado') title = '❌ Pedido Cancelado';

    await createNotification({
        id_usuario: userId,
        tipo: 'pago', // Reuse 'pago' icon (shopping bag/money) or 'sistema'
        titulo: title,
        mensaje: message,
        link: `/mi-perfil/compras`,
        metadata: { orderNumber, storeName, status: newStatus }
    });
};
